import { css } from '@emotion/react'
import {
  CornerRadius,
  cssVar,
  FontWeight,
  gemVar,
  LineWeight,
  Size,
  Spacing,
  ThemedColors,
  ThemedFocusIndicator,
  Timing,
  Typography,
} from '@opallabs/gem'
import { modernWithFallback } from '@opallabs/lambda'
import { type CalendarFilterMode } from '../types'

const rootClass = 'opal-calendar-filters'
export const selectors = {
  root: rootClass,
  categoryChevron: `${rootClass}-category-chevron`,
  categoryRow: `${rootClass}-category-row`,
  categoryList: `${rootClass}-category-list`,
  categorySearch: `${rootClass}-category-search`,
  count: `${rootClass}-count`,
  dateField: `${rootClass}-date-field`,
  dateFieldLabel: `${rootClass}-date-field-label`,
  dateFields: `${rootClass}-date-fields`,
  footer: `${rootClass}-footer`,
  footerSummary: `${rootClass}-footer-summary`,
  header: `${rootClass}-header`,
  headerTitle: `${rootClass}-header-title`,
  leaf: `${rootClass}-leaf`,
  leafControls: `${rootClass}-leaf-controls`,
  leafEmpty: `${rootClass}-leaf-empty`,
  /** A mark that points along the line, so it turns round right to left. */
  lineArrow: `${rootClass}-line-arrow`,
  listBlock: `${rootClass}-list-block`,
  loading: `${rootClass}-loading`,
  modeGroupLabel: `${rootClass}-mode-group-label`,
  modeHint: `${rootClass}-mode-hint`,
  modeSelect: `${rootClass}-mode-select`,
  modeTrigger: `${rootClass}-mode-trigger`,
  modeTriggerCompact: `${rootClass}-mode-trigger-compact`,
  option: `${rootClass}-option`,
  optionDot: `${rootClass}-option-dot`,
  optionGroup: `${rootClass}-option-group`,
  optionList: `${rootClass}-option-list`,
  optionMode: `${rootClass}-option-mode`,
  optionPath: `${rootClass}-option-path`,
  panes: `${rootClass}-panes`,
  parentCheck: `${rootClass}-parent-check`,
  parentChevron: `${rootClass}-parent-chevron`,
  parentCount: `${rootClass}-parent-count`,
  parentOpen: `${rootClass}-parent-open`,
  parentOption: `${rootClass}-parent-option`,
  parentRow: `${rootClass}-parent-row`,
  parentTier: `${rootClass}-parent-tier`,
  searchHeading: `${rootClass}-search-heading`,
  searchResults: `${rootClass}-search-results`,
  selectAll: `${rootClass}-select-all`,
  selectAllBox: `${rootClass}-select-all-box`,
  text: `${rootClass}-text`,
  tokenGroup: `${rootClass}-token-group`,
  tokenList: `${rootClass}-token-list`,
  tokenText: `${rootClass}-token-text`,
} as const

export const cssVariables = {
  /** How far the pane track is slid. `paneShifts` holds the three answers. */
  paneShift: `--${rootClass}-pane-shift`,
} as const

/**
 * How far to slide the pane track for each tier: the categories, one category's
 * parents, then what sits under one parent. Three panes share a track three
 * times the width of the drawer, so a tier is a third of the track along it.
 */
export const paneShifts = ['0', '-33.3333%', '-66.6667%'] as const

const headerBlockSize = 64
const footerBlockSize = 56

/** The design insets a category row 20px on every side; Gem has no token for it. */
const CATEGORY_INSET = Spacing.XS * 5
/** The inset of a search and the list under it. */
const LIST_INSET = Spacing.XS * 3
/** One checkable row. */
const ROW_BLOCK_SIZE = Spacing.XS * 8
/** A row a person can open: the design's Subcategory row, inset 20px. */
const PARENT_ROW_BLOCK_SIZE = Spacing.XS * 16
/** Any radius past half the height draws a round-ended pill. */
const PILL_RADIUS = 999
/** The category rows carry a bigger mark than the rows inside one: 20px. */
export const CATEGORY_ICON_SIZE = Spacing.XS * 5
/** Room for the widest date the picker prints, "Sep 30, 2026", and its icon. */
const DATE_BUTTON_INLINE_SIZE = Spacing.XS * 32
/** The stroke sits inside the design's measurements, so it comes off the padding. */
const inset = (padding: number) => padding - LineWeight.Small

/**
 * Centre a word by the letters it paints rather than by the leading around
 * them, which is what makes a name sit level with the 16px mark beside it.
 *
 * Gem's label styles carry this trim already, but it cannot reach the words
 * here: a bare string in a flex row is an anonymous item, and nothing can
 * style one. Every such word is wrapped in `selectors.text` instead, which is
 * how lapidary's `Pill` pairs a word with an icon.
 */
const inkTrim = {
  textBox: 'trim-both cap alphabetic',
  // Firefox has no `text-box` yet, so there a word keeps its leading and the
  // same nudge lapidary's `Pill` uses stands in for the trim.
  '@supports not (text-box-trim: trim-both)': { paddingBlockStart: 1 },
} as const

/**
 * Green for matching, red for hiding, wherever a mode is named: the mode
 * control, the menu behind it, and the heading over a group of tokens.
 */
export const modeMark = (mode: CalendarFilterMode) =>
  css({
    color: gemVar(
      mode === 'exclude' ? ThemedColors.IconError : ThemedColors.IconSuccess
    ),
    display: 'inline-flex',
  })

/** One row of the mode menu, which is drawn outside the drawer. */
export const modeOption = css({
  columnGap: Spacing.XS,
  [`.${selectors.text}`]: inkTrim,
})

/** The mode word in the design's weight: 14px Bold, which Gem has no style for. */
const modeWord = {
  ...Typography.T500,
  fontWeight: FontWeight.Bold,
} as const

/** A checkable row: 32px, the box inset 8px, the name 4px after it. */
const row = {
  ...Typography.T500,
  alignItems: 'center',
  blockSize: ROW_BLOCK_SIZE,
  borderRadius: CornerRadius.Small,
  boxSizing: 'border-box',
  color: gemVar(ThemedColors.TextPrimary),
  columnGap: Spacing.XS,
  cursor: 'pointer',
  display: 'flex',
  paddingInline: Spacing.S,

  '&:hover': { backgroundColor: gemVar(ThemedColors.Surface2) },
} as const

export const root = css({
  backgroundColor: gemVar(ThemedColors.Surface1),
  blockSize: '100%',
  display: 'flex',
  flexDirection: 'column',
  inlineSize: '100%',
  minBlockSize: 0,

  [`.${selectors.header}`]: {
    alignItems: 'center',
    backgroundColor: gemVar(ThemedColors.Surface2),
    blockSize: headerBlockSize,
    borderBlockEnd: `${LineWeight.Small}px solid ${gemVar(ThemedColors.StrokeSecondary1)}`,
    boxSizing: 'border-box',
    columnGap: Spacing.S,
    display: 'flex',
    flex: '0 0 auto',
    paddingInline: CATEGORY_INSET,

    // The title takes the row, so the close button stays at the end of the
    // header however long the title is.
    [`.${selectors.headerTitle}`]: {
      flex: 1,
      minInlineSize: 0,
    },

    h2: {
      ...Typography.H300,
      color: gemVar(ThemedColors.TextPrimary),
      margin: 0,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
  },

  [`.${selectors.lineArrow}:dir(rtl)`]: {
    transform: 'scaleX(-1)',
  },

  [`.${selectors.panes}`]: {
    display: 'flex',
    flex: 1,
    inlineSize: '300%',
    minBlockSize: 0,
    transform: `translateX(${cssVar(cssVariables.paneShift)})`,
    transition: `transform ${Timing.Default}ms ease`,

    // A transform is physical, and right to left the panes run from the right
    // edge, so the next one is brought in from the other side.
    '&:dir(rtl)': {
      transform: `translateX(calc(-1 * ${cssVar(cssVariables.paneShift)}))`,
    },

    '> *': {
      blockSize: '100%',
      flex: '0 0 33.3333%',
      minBlockSize: 0,
      ...modernWithFallback(
        [{ overflowBlock: 'auto' }, { overflowY: 'auto' }],
        'overflowBlock: auto'
      ),
    },
    // Belt and braces behind the `inert` attribute the drawer sets: a browser
    // that does not know `inert` still cannot put focus inside a hidden pane.
    //
    // Only the hidden state carries the transition, so a pane takes the whole
    // slide to go, and none of it to arrive. It has to arrive at once: the
    // drawer hands focus to a row in the arriving pane as it opens, and a
    // browser refuses focus to anything still `visibility: hidden`.
    '> *[inert]': {
      visibility: 'hidden',
      transition: `visibility ${Timing.Default}ms`,
    },

    '@media (prefers-reduced-motion: reduce)': {
      transition: 'none',
      '> *[inert]': { transition: 'none' },
    },
  },

  // A line under each category, and none under the tokens inside one: only the
  // rows of this list are separated, not everything that sits in them.
  [`.${selectors.categoryList}`]: {
    listStyle: 'none',
    margin: 0,
    padding: 0,

    '> li': {
      borderBlockEnd: `${LineWeight.Small}px solid ${gemVar(ThemedColors.StrokeSecondary1)}`,
      padding: CATEGORY_INSET,
      paddingBlockEnd: inset(CATEGORY_INSET),
    },
  },

  // The way out of the drawer, the way out of every filter in it, and what the
  // filters have come to. It is a child of the drawer rather than of the pane
  // track, so it stands still while the panes slide behind it.
  [`.${selectors.footer}`]: {
    alignItems: 'center',
    backgroundColor: gemVar(ThemedColors.Surface1),
    blockSize: footerBlockSize,
    borderBlockStart: `${LineWeight.Small}px solid ${gemVar(ThemedColors.StrokeSecondary2)}`,
    boxSizing: 'border-box',
    columnGap: Spacing.S,
    display: 'flex',
    flex: '0 0 auto',
    paddingInline: LIST_INSET,
  },
  // The counts sit at the far end of the row, however wide the buttons are.
  [`.${selectors.footerSummary}`]: {
    ...Typography.T500,
    alignItems: 'center',
    color: gemVar(ThemedColors.TextSecondary),
    columnGap: Spacing.S,
    display: 'flex',
    marginInlineStart: 'auto',

    // Each count and its mark, and only those: the word inside one is a text
    // box, and a flex box has no line for the trim to cut.
    '> span': {
      alignItems: 'center',
      columnGap: Spacing.XS,
      display: 'inline-flex',
    },
  },

  // The whole row opens the category, edge to edge, and lifts a shade when the
  // pointer is over it. The button reaches back out over the inset its list
  // item keeps, so the tokens under it still line up with its title.
  [`.${selectors.categoryRow}`]: {
    ...Typography.H50,
    alignItems: 'center',
    background: 'none',
    border: 'none',
    color: gemVar(ThemedColors.TextPrimary),
    columnGap: Spacing.S,
    cursor: 'pointer',
    display: 'flex',
    // A button is only as wide as its words unless told otherwise.
    inlineSize: `calc(100% + ${CATEGORY_INSET * 2}px)`,
    margin: `-${CATEGORY_INSET}px -${CATEGORY_INSET}px -${inset(CATEGORY_INSET)}px`,
    minBlockSize: Spacing.L,
    padding: CATEGORY_INSET,
    paddingBlockEnd: inset(CATEGORY_INSET),
    textAlign: 'start',

    '&:hover': { backgroundColor: gemVar(ThemedColors.Surface2) },
    '&:focus-visible': ThemedFocusIndicator,
  },
  // The chevron is the far end of the row, in the 24px box the design gives
  // its button, so its mark lands where the close mark does in the header.
  [`.${selectors.categoryChevron}`]: {
    alignItems: 'center',
    display: 'inline-flex',
    marginInlineStart: 'auto',
    padding: Spacing.XS,
  },

  // A number small enough to read as a mark on the row rather than as another
  // word in it: a 16px pill that grows sideways past one digit.
  [`.${selectors.count}`]: {
    ...Typography.TL300,
    // The design sets the pill in Medium; Gem's 12px style is Semibold.
    fontWeight: FontWeight.Medium,
    alignItems: 'center',
    backgroundColor: gemVar(ThemedColors.Surface3),
    blockSize: Spacing.M,
    borderRadius: PILL_RADIUS,
    boxSizing: 'border-box',
    color: gemVar(ThemedColors.TextPrimary),
    display: 'inline-flex',
    flex: 'none',
    justifyContent: 'center',
    minInlineSize: Spacing.M,
    paddingInline: Spacing.XS,
  },

  [`.${selectors.text}`]: inkTrim,

  // What is chosen, 16px under the row and 24px between one group and the next.
  [`.${selectors.tokenGroup}`]: {
    display: 'flex',
    flexDirection: 'column',
    marginBlockStart: Spacing.L,
    rowGap: Spacing.S,
  },
  [`.${selectors.categoryRow} + .${selectors.tokenGroup}`]: {
    marginBlockStart: Spacing.M,
  },
  // A rule between the heading and its tokens, and 8px round every token.
  [`.${selectors.tokenList}`]: {
    borderBlockStart: `${LineWeight.Small}px solid ${gemVar(ThemedColors.StrokeSecondary2)}`,
    columnGap: Spacing.S,
    display: 'flex',
    flexWrap: 'wrap',
    listStyle: 'none',
    margin: 0,
    padding: `${Spacing.S}px 0 0`,
    rowGap: Spacing.S,
  },
  // The marks in front of a token's words: a colour, a channel.
  [`.${selectors.tokenText}`]: {
    alignItems: 'center',
    columnGap: Spacing.XS,
    display: 'inline-flex',
    minInlineSize: 0,
  },
  [`.${selectors.leaf}`]: {
    display: 'flex',
    flexDirection: 'column',
  },
  [`.${selectors.dateFields}`]: {
    display: 'flex',
    flexDirection: 'column',
    padding: Spacing.M,
    rowGap: Spacing.M,
  },
  // The label and its date read left to right on one line. The button is as
  // wide as the longest date it can hold, so the two dates line up whether
  // they are set or not.
  [`.${selectors.dateField}`]: {
    alignItems: 'center',
    columnGap: Spacing.S,
    display: 'flex',
    justifyContent: 'space-between',

    '.satellite-date-picker': { inlineSize: DATE_BUTTON_INLINE_SIZE },
  },
  [`.${selectors.dateFieldLabel}`]: {
    ...Typography.H50,
    color: gemVar(ThemedColors.TextPrimary),
  },
  // The mode control sits 8px inside the drawer, on a panel of its own.
  [`.${selectors.leafControls}`]: {
    padding: Spacing.S,
  },
  // The control and what it means, side by side in one panel: the design reads
  // the hint as part of the control rather than as a note under it.
  [`.${selectors.modeSelect}`]: {
    alignItems: 'center',
    backgroundColor: gemVar(ThemedColors.Surface2),
    border: `${LineWeight.Small}px solid ${gemVar(ThemedColors.StrokeSecondary1)}`,
    borderRadius: CornerRadius.Small,
    columnGap: LIST_INSET,
    display: 'flex',
    padding: inset(LIST_INSET),
  },
  // A 32px select button: the mark, the word and the chevron, all 16px.
  [`.${selectors.modeTrigger}`]: {
    ...modeWord,
    alignItems: 'center',
    backgroundColor: gemVar(ThemedColors.Surface1),
    blockSize: ROW_BLOCK_SIZE,
    border: `${LineWeight.Small}px solid ${gemVar(ThemedColors.StrokeSecondary2)}`,
    borderRadius: CornerRadius.Small,
    boxSizing: 'border-box',
    color: gemVar(ThemedColors.TextPrimary),
    columnGap: Spacing.XS,
    cursor: 'pointer',
    display: 'flex',
    flex: 'none',
    paddingInline: Spacing.S,

    '&:disabled': { cursor: 'default', opacity: 0.5 },
    '&:focus-visible': ThemedFocusIndicator,
  },
  // The same control sized for a row it shares with a box, a name and a
  // chevron: 24px, and no panel around it.
  [`.${selectors.modeTriggerCompact}`]: {
    blockSize: Spacing.L,
    paddingInline: Spacing.XS,
  },
  [`.${selectors.modeHint}`]: {
    ...Typography.T300,
    ...inkTrim,
    color: gemVar(ThemedColors.TextSecondary),
  },
  // What the tokens under it do, said once for all of them: a 16px pill of the
  // mark and the word.
  [`.${selectors.modeGroupLabel}`]: {
    ...Typography.TL300,
    fontWeight: FontWeight.Medium,
    alignItems: 'center',
    blockSize: Spacing.M,
    color: gemVar(ThemedColors.TextPrimary),
    columnGap: Spacing.XS / 2,
    display: 'flex',
  },
  // A direct child of the drawer, never of the sliding pane pair, so the dots
  // sit in the middle of what a person actually sees.
  [`.${selectors.loading}`]: {
    alignItems: 'center',
    display: 'flex',
    flex: 1,
    justifyContent: 'center',
    padding: Spacing.L,
  },
  [`.${selectors.leafEmpty}`]: {
    ...Typography.T300,
    color: gemVar(ThemedColors.TextSecondary),
    padding: Spacing.L,
    textAlign: 'center',
  },

  // The search over every category sits where a tier's own search does.
  [`.${selectors.categorySearch}`]: {
    padding: LIST_INSET,
  },
  // What that search found: a heading per category and its rows under it, the
  // rows inset exactly as they are inside a tier.
  [`.${selectors.searchResults}`]: {
    display: 'flex',
    flexDirection: 'column',
    listStyle: 'none',
    margin: 0,
    padding: `0 ${LIST_INSET}px ${LIST_INSET}px`,
    rowGap: Spacing.M,
  },
  [`.${selectors.searchHeading}`]: {
    ...Typography.H50,
    alignItems: 'center',
    color: gemVar(ThemedColors.TextPrimary),
    columnGap: Spacing.S,
    display: 'flex',
    margin: 0,
    padding: Spacing.S,
  },
  // The row a value sits under, quieter than the value.
  [`.${selectors.optionPath}`]: {
    color: gemVar(ThemedColors.TextSecondary),
    flex: 'none',
  },
  // The minus on a row that hides what it holds, at the far end of the row.
  [`.${selectors.optionMode}`]: {
    display: 'inline-flex',
    marginInlineStart: 'auto',
  },

  // A search and the rows it filters, 12px inside the drawer and 12px apart.
  [`.${selectors.listBlock}`]: {
    display: 'flex',
    flexDirection: 'column',
    padding: LIST_INSET,
    rowGap: LIST_INSET,
  },
  // Rows a person opens run the drawer's whole width, rule to rule, so only the
  // search keeps the inset.
  [`.${selectors.listBlock}.${selectors.parentTier}`]: {
    paddingBlockEnd: 0,
    paddingInline: 0,

    '> .lapidary-searchInput': { marginInline: LIST_INSET },
    [`.${selectors.optionList}`]: { rowGap: 0 },
    // Every row on the tier is drawn as a category row, whether or not it has
    // anything under it: Select all, and a channel with no accounts, sit
    // between rows that open, and a tier reads as one list or not at all.
    [`.${selectors.selectAll}, .${selectors.option}`]: {
      blockSize: PARENT_ROW_BLOCK_SIZE,
      borderBlockEnd: `${LineWeight.Small}px solid ${gemVar(ThemedColors.StrokeSecondary1)}`,
      borderRadius: 0,
      columnGap: Spacing.S,
      padding: CATEGORY_INSET,
      paddingBlockEnd: inset(CATEGORY_INSET),

      '&:hover': { backgroundColor: 'transparent' },
    },
    [`.${selectors.option}`]: {
      ...Typography.H50,
      paddingInlineEnd: CATEGORY_INSET + Spacing.L + Spacing.XS,
    },
  },
  [`.${selectors.optionList}`]: {
    display: 'flex',
    flexDirection: 'column',
    listStyle: 'none',
    margin: 0,
    padding: 0,
    rowGap: Spacing.XS,

    ul: {
      display: 'flex',
      flexDirection: 'column',
      listStyle: 'none',
      margin: 0,
      padding: 0,
      rowGap: Spacing.XS,
    },
  },
  // A child's box sits under its parent's name, not under its box, so the
  // parent-child tree reads at a glance even when rows carry no icon.
  [`.${selectors.optionGroup}`]: {
    paddingInlineStart: Size.Icon + Spacing.XS,
  },
  [`.${selectors.option}`]: row,
  // A part-chosen row cannot be a Checkbox, so it is a button wearing the same
  // row as every option beside it.
  [`.${selectors.parentOption}`]: {
    background: 'none',
    border: 'none',
    inlineSize: '100%',
    textAlign: 'start',

    '&:focus-visible': ThemedFocusIndicator,
  },
  // A parent row is two controls side by side: the box chooses the whole
  // parent, and the rest of the row opens what sits under it. They cannot be
  // one control, and a button inside a button is a button nothing can reach.
  //
  // It is drawn as the design's category row: 20px inside the drawer, a rule
  // under it, and the chevron at the far end. The chevron belongs to the
  // button that opens the row but has to come after the mode control the row
  // may carry, so it is lifted to the trailing edge and the row keeps a 24px
  // box and a 4px gap clear for it.
  //
  // The row's inset belongs to its two controls rather than to the row, so
  // everything but the box is the button that opens the row: the whole row
  // is the target, and it lifts a shade while the pointer is over that part.
  [`.${selectors.parentRow}`]: {
    alignItems: 'center',
    borderBlockEnd: `${LineWeight.Small}px solid ${gemVar(ThemedColors.StrokeSecondary1)}`,
    display: 'flex',
    paddingInlineStart: CATEGORY_INSET,
    position: 'relative',

    [`&:has(> .${selectors.parentOpen}:hover)`]: {
      backgroundColor: gemVar(ThemedColors.Surface2),
    },
    // A mode control ends the row 4px short of the chevron's box.
    [`.${selectors.modeTriggerCompact}`]: {
      marginInlineEnd: CATEGORY_INSET + Spacing.L + Spacing.XS,
    },
    [`.${selectors.parentOpen}:not(:last-child)`]: {
      paddingInlineEnd: Spacing.XS,
    },
  },
  [`.${selectors.parentCheck}`]: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    padding: `${CATEGORY_INSET}px 0 ${inset(CATEGORY_INSET)}px`,

    '&:focus-visible': ThemedFocusIndicator,
  },
  [`.${selectors.parentOpen}`]: {
    ...Typography.H50,
    alignItems: 'center',
    background: 'none',
    border: 'none',
    color: gemVar(ThemedColors.TextPrimary),
    columnGap: Spacing.S,
    cursor: 'pointer',
    display: 'flex',
    flex: 1,
    // The design's 64px row, less the rule under it: the padding and a 24px
    // line, whatever the name's own line height comes to.
    minBlockSize: CATEGORY_INSET + Spacing.L + inset(CATEGORY_INSET),
    boxSizing: 'border-box',
    minInlineSize: 0,
    padding: `${CATEGORY_INSET}px ${CATEGORY_INSET + Spacing.L + Spacing.XS}px ${inset(CATEGORY_INSET)}px ${Spacing.S}px`,
    textAlign: 'start',

    '&:focus-visible': ThemedFocusIndicator,
  },
  [`.${selectors.parentCount}`]: {
    display: 'inline-flex',
  },
  [`.${selectors.parentChevron}`]: {
    alignItems: 'center',
    display: 'flex',
    insetBlock: 0,
    insetInlineEnd: CATEGORY_INSET + Spacing.XS,
    position: 'absolute',
  },
  [`.${selectors.optionDot}`]: {
    blockSize: Spacing.S,
    borderRadius: '50%',
    flex: 'none',
    inlineSize: Spacing.S,
  },

  // The same row as any option below it.
  [`.${selectors.selectAll}`]: {
    ...row,
    background: 'none',
    border: 'none',
    inlineSize: '100%',
    textAlign: 'start',

    '&:focus-visible': ThemedFocusIndicator,
  },
  // Lapidary's Checkbox cannot show a part-chosen state, so every row that can
  // be part-chosen draws its own box. It has to be the same picture as the
  // Checkbox on the rows beside it, so it takes the same numbers and the same
  // colours: `StrokeSecondary1` and `Primary6` are what the Checkbox's own
  // `Surface.N300` and `Surface.A200` come to in both themes.
  [`.${selectors.selectAllBox}`]: {
    alignItems: 'center',
    blockSize: Spacing.M,
    borderColor: gemVar(ThemedColors.StrokeSecondary1),
    borderRadius: Size.Rounding,
    borderStyle: 'solid',
    borderWidth: Spacing.XS / 2,
    // The Checkbox draws its border inside its 16px; without this the border
    // adds itself outside and the box lands 4px bigger than every row's.
    boxSizing: 'border-box',
    display: 'inline-flex',
    flex: 'none',
    inlineSize: Spacing.M,
    justifyContent: 'center',

    '&[aria-checked="true"], &[aria-checked="mixed"]': {
      backgroundColor: gemVar(ThemedColors.Primary6),
      borderColor: gemVar(ThemedColors.Primary6),
      color: gemVar(ThemedColors.TextContrast),
    },
    '&[aria-checked="mixed"]::after': {
      backgroundColor: gemVar(ThemedColors.TextContrast),
      blockSize: LineWeight.Large,
      borderRadius: LineWeight.Large,
      content: '""',
      inlineSize: Spacing.S,
    },
  },
})
